import React from 'react';
import { usePage } from '@inertiajs/react';

import DocumentCard, { type DocumentItem } from '@/components/public/cards/DocumentCard';
import PageHero from '@/components/public/sections/PageHero';
import Seo from '@/components/public/Seo';
import CategoryFilter from '@/components/public/ui/CategoryFilter';
import Pagination from '@/components/public/ui/Pagination';
import SearchBar from '@/components/public/ui/SearchBar';
import Section from '@/components/public/ui/Section';
import YearFilter from '@/components/public/ui/YearFilter';
import { DocumentsPageDetails } from '@/data/pages/documents/details';
import { trackUmamiEvent } from '@/lib/analytics';

const ITEMS_PER_PAGE = 6;

type Props = {
  documents: DocumentItem[];
  categories: string[];
  initialCategory?: string | null;
};

function parseInitialCategories(
  initialCategory: string | null | undefined,
  validCategories: string[],
): string[] {
  if (!initialCategory) return ['Todos'];
  const requested = initialCategory
    .split(',')
    .map((s) => s.trim())
    .filter((s) => validCategories.includes(s));
  return requested.length > 0 ? requested : ['Todos'];
}

function filterDocuments(
  documents: DocumentItem[],
  activeCategories: string[],
  selectedYear: number | null,
  searchQuery: string,
): DocumentItem[] {
  let result = documents;

  if (!activeCategories.includes('Todos')) {
    result = result.filter((d) => activeCategories.includes(d.category));
  }

  if (selectedYear !== null) {
    result = result.filter((d) => d.year === selectedYear);
  }

  if (searchQuery.trim()) {
    const query = searchQuery.toLowerCase();
    result = result.filter(
      (d) =>
        d.title.toLowerCase().includes(query) ||
        (d.description ?? '').toLowerCase().includes(query),
    );
  }

  return result;
}

export default function Documents() {
  const { documents, categories, initialCategory } = usePage<Props>().props;

  const [activeCategories, setActiveCategories] = React.useState<string[]>(() =>
    parseInitialCategories(initialCategory, categories),
  );
  const [searchQuery, setSearchQuery] = React.useState('');
  const [selectedYear, setSelectedYear] = React.useState<number | null>(null);
  const [currentPage, setCurrentPage] = React.useState(1);

  const years = React.useMemo(() => {
    const uniqueYears = [...new Set(documents.map((d) => d.year))];
    return uniqueYears.sort((a, b) => b - a);
  }, [documents]);

  const filteredDocuments = React.useMemo(
    () => filterDocuments(documents, activeCategories, selectedYear, searchQuery),
    [documents, activeCategories, selectedYear, searchQuery],
  );

  React.useEffect(() => {
    const query = searchQuery.trim();
    if (query.length < 3) return;

    const timeoutId = window.setTimeout(() => {
      trackUmamiEvent('documents_search', {
        query_length: query.length,
        result_count: filteredDocuments.length,
      });
    }, 600);

    return () => window.clearTimeout(timeoutId);
  }, [filteredDocuments.length, searchQuery]);

  const totalPages = Math.ceil(filteredDocuments.length / ITEMS_PER_PAGE);
  const paginatedDocuments = filteredDocuments.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  const handleCategoryChange = (cats: string[]) => {
    setActiveCategories(cats);
    setCurrentPage(1);
    trackUmamiEvent('documents_filter', {
      categories: cats.join(','),
      year: selectedYear ?? 'all',
      result_count: filterDocuments(documents, cats, selectedYear, searchQuery).length,
    });
  };

  const handleYearChange = (year: number | null) => {
    setSelectedYear(year);
    setCurrentPage(1);
    trackUmamiEvent('documents_filter', {
      categories: activeCategories.join(','),
      year: year ?? 'all',
      result_count: filterDocuments(documents, activeCategories, year, searchQuery).length,
    });
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    trackUmamiEvent('documents_page_change', {
      page,
      total_pages: totalPages,
      result_count: filteredDocuments.length,
    });
  };

  return (
    <>
      <Seo
        title="Documentos"
        description="Consulte atas, editais, regulamentos e outros documentos públicos da Junta de Freguesia."
      />
      <PageHero
        badge={DocumentsPageDetails.badge}
        title={DocumentsPageDetails.title}
        accentWord={DocumentsPageDetails.accentWord}
        subtitle={DocumentsPageDetails.subtitle}
      />
      <Section spacing="md">
        <div className="mb-8 flex flex-col items-start gap-4 md:flex-row md:items-center">
          <SearchBar
            placeholder="Procurar por título ou palavra-chave…"
            ariaLabel="Pesquisar documentos por título ou palavra-chave"
            value={searchQuery}
            onChange={handleSearchChange}
            className="flex-1"
          />
          <CategoryFilter
            categories={categories}
            active={activeCategories}
            onChange={handleCategoryChange}
          />
          {years.length > 1 && (
            <YearFilter
              years={years}
              active={selectedYear}
              onChange={handleYearChange}
              label="Ano"
            />
          )}
        </div>
        {paginatedDocuments.length > 0 ? (
          <>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {paginatedDocuments.map((doc) => (
                <DocumentCard key={doc.id} {...doc} />
              ))}
            </div>
            {totalPages > 1 && (
              <div className="mt-8 flex justify-center">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onChange={handlePageChange}
                />
              </div>
            )}
          </>
        ) : (
          <div className="surface-card p-12 text-center">
            <p className="text-on-surface-light text-lg">
              Nenhum documento encontrado para os filtros selecionados.
            </p>
          </div>
        )}
      </Section>
    </>
  );
}
